// deps
import { useState, useEffect, useContext, useRef } from "react";

// internal-deps
import { Option, Some } from "@swiggy-private/js-fp";
import { useMountedRef } from "@swiggy-private/core-ui";
import { VALID_PAYMENT_GROUP_TYPES } from "@swiggy-private/payment-core";

// interfaces
import { BankListData, GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";

// contexts
import {
    ApiContext,
    ConfigContext,
    useSessionStorage,
} from "@swiggy-private/checkout-core";
import { NetbankingMoreBanksContextValue } from "@payment-client/contexts/NetbankingMoreBanksContext";

// helpers
import { getPaymentGroup } from "@payment-client/helpers/PaymentHelper";
import { isBusinessLineSwiggyMoney } from "@payment-client/helpers/Misc";

// constants
import { NETBANKING_LIST } from "@payment-client/constants/Options";
import { SWIGGY_MONEY_MORE_BANKS_LIST_KEY, DEFAULT_MORE_BANKS_LIST_KEY } from "@payment-client/constants/StorageKeys";
import { getBanksListPublic } from "@payment-client/apis/publicPayment";

async function fetchMoreBanksList(
    apiHost: string,
): Promise<Option<BankListData>> {
    return getBanksListPublic(apiHost).then(data => Some(data));
}

const useMoreBanks = (
    isTurboPaymentMethod: boolean,
    paymentResponse?: GetPaymentMethodsResponse | null,
): NetbankingMoreBanksContextValue => {
    const apiContextValue = useContext(ApiContext);
    const { cachePaymentData } = useContext(ConfigContext);
    const apiCallRef = useRef(false);
    const isMountedRef = useMountedRef();
    const bankListKey = isBusinessLineSwiggyMoney() ? SWIGGY_MONEY_MORE_BANKS_LIST_KEY : DEFAULT_MORE_BANKS_LIST_KEY;
    const [dataInCache, saveDataInCache] = useSessionStorage<BankListData["bankList"] | null>(bankListKey, null);
    const [loading, setLoading] = useState(true);
    const [bankList, setBankList] = useState<BankListData["bankList"]>([]);

    const apiHost = apiContextValue.host.unwrap();
    const canCachePaymentData = cachePaymentData?.unwrapOr(true);

    useEffect(() => {
        // to skip get-all-banks api call in case of turbocheckout
        if (isTurboPaymentMethod || bankList.length > 0) {
            setLoading(false);
            return;
        }

        if (dataInCache && loading) {
            setLoading(false);
            setBankList(dataInCache);
            return;
        }

        const netbankingGroup = paymentResponse
            ? getPaymentGroup(paymentResponse, VALID_PAYMENT_GROUP_TYPES.NETBANKING)
            : undefined;
        if (netbankingGroup == null || !netbankingGroup.enabled || !loading || apiCallRef.current) {
            return;
        }

        apiCallRef.current = true;
        fetchMoreBanksList(apiHost)
            .then(BankListData => {
                apiCallRef.current = false;
                if (isMountedRef.current) {
                    const data = BankListData.isSome() ? BankListData.unwrap().bankList : null;

                    canCachePaymentData && saveDataInCache(data);
                    setLoading(false);
                    setBankList(data ?? NETBANKING_LIST);
                }
            })
            .catch(() => {
                apiCallRef.current = false;
                if (isMountedRef.current) {
                    canCachePaymentData && saveDataInCache(null);
                    setLoading(false);
                    // set with default list as fallback
                    setBankList(NETBANKING_LIST);
                }
            });
    }, [
        dataInCache,
        canCachePaymentData,
        apiHost,
        isMountedRef,
        saveDataInCache,
        paymentResponse,
        loading,
        bankList,
        isTurboPaymentMethod,
    ]);

    return { bankList };
};

export default useMoreBanks;
